"use client";

import * as React from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./Tooltip";
import styles from "./TruncateWithTooltip.module.css";

type Side = "top" | "right" | "bottom" | "left";

type TruncateBaseProps = {
  /** Already-translated string to render. The component itself does NOT call t(). */
  text: string;
  /** Render element. `div` (default) for block contexts, `span` for inline. */
  as?: "div" | "span";
  /** Tooltip side; forwarded to Radix. Defaults to "top". */
  side?: Side;
  /** Tooltip open delay in ms; forwarded to Radix. Defaults to 200ms. */
  delayDuration?: number;
  /** Optional extra className applied to the truncated element. */
  className?: string;
};

// Extra HTML attributes for the chosen element (without children — `text` owns it).
type TruncateWithTooltipProps = TruncateBaseProps &
  Omit<React.HTMLAttributes<HTMLElement>, "children" | "className">;

// TODO(#2284 follow-up): multi-line truncation via `-webkit-line-clamp`
// is intentionally out of scope here. All currently-known call sites
// (#2294, #2296, #2298) are single-line ellipsis. If multi-line lands,
// extend the API with `lines?: number` and a separate CSS class — do not
// hack it into the existing single-line path.

/**
 * Truncate a string with ellipsis. Only renders a tooltip when the text
 * actually overflows its container — otherwise behaves as a plain
 * truncated span/div with zero extra DOM.
 *
 * Overflow is measured against `scrollWidth > clientWidth` on mount and
 * re-measured via `ResizeObserver` whenever the container is resized.
 *
 * The caller is responsible for translating `text` via `t()` — this
 * component does not import i18next.
 */
export const TruncateWithTooltip = React.forwardRef<HTMLElement, TruncateWithTooltipProps>(
  function TruncateWithTooltip(
    {
      text,
      as = "div",
      side = "top",
      delayDuration = 200,
      className,
      ...rest
    },
    forwardedRef,
  ) {
    const innerRef = React.useRef<HTMLElement | null>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);

    // Merge forwarded ref + internal ref.
    const setRefs = React.useCallback(
      (node: HTMLElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    // Measure on mount + whenever the element resizes.
    React.useEffect(() => {
      const node = innerRef.current;
      if (!node) return;

      const measure = (): void => {
        // Use a tolerance of 1px to avoid sub-pixel false positives.
        setIsOverflowing(node.scrollWidth - node.clientWidth > 1);
      };

      measure();

      // ResizeObserver is supported in all browsers we target (and on iOS/Android via Capacitor v8).
      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(() => measure());
      observer.observe(node);
      return () => observer.disconnect();
    }, [text]);

    const mergedClassName = `${styles.truncate} ${className ?? ""}`.trim();

    // Render the truncated element. We type the inner element as
    // HTMLDivElement | HTMLSpanElement; both expose scrollWidth/clientWidth.
    const truncatedElement =
      as === "span" ? (
        <span
          ref={setRefs as React.Ref<HTMLSpanElement>}
          className={mergedClassName}
          {...(rest as React.HTMLAttributes<HTMLSpanElement>)}
        >
          {text}
        </span>
      ) : (
        <div
          ref={setRefs as React.Ref<HTMLDivElement>}
          className={mergedClassName}
          {...(rest as React.HTMLAttributes<HTMLDivElement>)}
        >
          {text}
        </div>
      );

    // No overflow → no tooltip wiring. Avoids showing a tooltip with the
    // same text already visible on screen.
    if (!isOverflowing) {
      return truncatedElement;
    }

    return (
      <Tooltip delayDuration={delayDuration}>
        {/* `asChild` lets the trigger inherit the existing truncated element
            without injecting an extra wrapper. */}
        <TooltipTrigger asChild>{truncatedElement}</TooltipTrigger>
        <TooltipContent side={side}>{text}</TooltipContent>
      </Tooltip>
    );
  },
);
